import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, User, Calendar, BookMarked } from "lucide-react";
import { findArticle } from "@/data/articles";

const Article = () => {
  const { slug } = useParams();
  const article = findArticle(slug);

  if (!article) return <Navigate to="/articole" replace />;

  return (
    <div data-testid="article-page">
      {/* HEADER */}
      <section className="max-w-[1000px] mx-auto px-6 lg:px-12 pt-16 pb-8" data-testid="article-header">
        <Link
          to="/articole"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[color:var(--ink-soft)] hover:text-[color:var(--gold)]"
          data-testid="article-back-link"
        >
          <ArrowLeft size={14} /> Înapoi la articole
        </Link>

        <div className="mt-10 fade-in">
          <div className="flex items-center gap-3 mb-5">
            <span className="eyebrow text-[color:var(--gold)]">{article.category}</span>
            <span className="w-8 h-px bg-[color:var(--gold)]/50" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink-soft)]">{article.date}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[color:var(--ink)]" data-testid="article-title">
            {article.title}
          </h1>

          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm">
            <div className="flex items-center gap-2 text-[color:var(--ink-soft)]">
              <User size={15} className="text-[color:var(--gold)]" />
              <span className="font-display-italic text-[color:var(--ink)]" data-testid="article-author">{article.author}</span>
            </div>
            <div className="flex items-center gap-2 text-[color:var(--ink-soft)]">
              <BookMarked size={15} className="text-[color:var(--gold)]" />
              <span data-testid="article-source">{article.publishedIn}</span>
            </div>
            <div className="flex items-center gap-2 text-[color:var(--ink-soft)]">
              <Calendar size={15} className="text-[color:var(--gold)]" />
              <time dateTime={article.dateISO}>{article.date}</time>
            </div>
          </div>
        </div>
      </section>

      <div className="rule max-w-[1000px] mx-auto" />

      {/* BODY */}
      <article className="max-w-[820px] mx-auto px-6 lg:px-0 py-16" data-testid="article-body">
        {article.sections?.map((s, i) => (
          <section key={i} className="mb-12" data-testid={`article-section-${i}`}>
            <h2 className="font-display text-3xl text-[color:var(--ink)] leading-tight">{s.heading}</h2>
            <p className={`mt-5 text-[17px] leading-[1.8] text-[color:var(--ink-soft)] ${i === 0 ? "drop-cap" : ""}`}>
              {s.body}
            </p>
          </section>
        ))}

        {article.keyPoints?.length > 0 && (
          <section className="mt-16" data-testid="article-key-points">
            <div className="eyebrow">Puncte-cheie</div>
            <h2 className="font-display text-3xl mt-3 text-[color:var(--ink)]">
              {article.keyPointsHeading || "Ideile principale ale studiului"}
            </h2>

            <ol className="mt-10 space-y-8">
              {article.keyPoints.map((k, i) => (
                <li key={i} className="grid grid-cols-12 gap-6" data-testid={`article-key-point-${i}`}>
                  <div className="col-span-2 sm:col-span-1">
                    <div className="font-display text-3xl text-[color:var(--gold)] leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="col-span-10 sm:col-span-11">
                    <h3 className="font-display text-xl text-[color:var(--ink)] leading-snug">{k.title}</h3>
                    <p className="mt-3 text-[16px] leading-[1.75] text-[color:var(--ink-soft)]">{k.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {article.siteUrl && (
          <aside className="mt-16 border-l-2 border-[color:var(--gold)] pl-6 sm:pl-8" data-testid="article-site-cta">
            <div className="eyebrow">Platforma</div>
            <p className="mt-3 text-[17px] leading-[1.8] text-[color:var(--ink-soft)]">
              Platforma este disponibilă online la adresa{" "}
              <a
                href={article.siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-display-italic text-[color:var(--ink)] underline decoration-[color:var(--gold)] decoration-2 underline-offset-4 hover:text-[color:var(--gold)] transition-colors"
                data-testid="article-site-link"
              >
                {article.siteLabel || article.siteUrl}
              </a>
              .
            </p>
          </aside>
        )}

        {article.externalUrl && (
          <aside className="mt-20 gold-border bg-[color:var(--cream)]/60 p-8 sm:p-10" data-testid="article-external-cta">
            <div className="eyebrow">{article.externalLabel || "Textul integral"}</div>
            <h3 className="font-display text-2xl sm:text-3xl mt-3 text-[color:var(--ink)] leading-tight">
              Citiți materialul complet pe {article.publishedIn}.
            </h3>
            <p className="mt-4 text-[color:var(--ink-soft)] text-[15px]">
              {article.externalNote || (
                <>
                  Un studiu scris de <span className="font-display-italic text-[color:var(--ink)]">{article.author}</span>,
                  disponibil în versiunea integrală, cu note și referințe, pe portalul juridice.ro.
                </>
              )}
            </p>
            <a
              href={article.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-8"
              data-testid="article-external-link"
            >
              Citește articolul complet <ArrowUpRight size={16} />
            </a>
          </aside>
        )}
      </article>
    </div>
  );
};

export default Article;
